import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnChanges,
  signal,
  SimpleChanges,
} from '@angular/core';
import { Params } from '@angular/router';
import {
  Alert,
  AlertSubType,
  ChargingSessionAlertDetails,
  ChargingSessionCurrentType,
} from '@monitor/alerting/data/alerting.models';
import { TranslateService } from '@ngx-translate/core';
import { ChatbotService } from '@shared/services/chatbot/chatbot.service';
import { ButtonColor, ButtonSize } from '@widgets/eop-button/eop-button.component';
import { ChipColor, ChipSize } from '@widgets/eop-chip/data/eop-chip.data';
import { environment } from 'environments/environment';

const ISSUE_DETAILS_KEYS: Partial<Record<AlertSubType, string>> = {
  [AlertSubType.CS_MISSED_REVENUE]: 'ALERTING.ISSUE_DETAILS_TEXT_CS_MISSED_REVENUE',
  [AlertSubType.CS_LOST_REVENUE]: 'ALERTING.ISSUE_DETAILS_TEXT_CS_LOST_REVENUE',
  [AlertSubType.CS_INACTIVITY]: 'ALERTING.ISSUE_DETAILS_TEXT_CS_INACTIVITY',
  [AlertSubType.CS_INACTIVITY_PT]: 'ALERTING.ISSUE_DETAILS_TEXT_CS_INACTIVITY_PT',
};

const VAYLA_PROMPT_KEYS: Partial<Record<AlertSubType, string>> = {
  [AlertSubType.CS_MISSED_REVENUE]: 'ALERTING.VAYLA_PROMPT_CS_MISSED_REVENUE',
  [AlertSubType.CS_LOST_REVENUE]: 'ALERTING.VAYLA_PROMPT_CS_LOST_REVENUE',
  [AlertSubType.CS_INACTIVITY]: 'ALERTING.VAYLA_PROMPT_CS_INACTIVITY',
  [AlertSubType.CS_INACTIVITY_PT]: 'ALERTING.VAYLA_PROMPT_CS_INACTIVITY_PT',
};

const KNOWLEDGE_BASE_PATHS: Partial<Record<AlertSubType, string>> = {
  [AlertSubType.CS_MISSED_REVENUE]: '/s/article/status-active-charging-session',
  [AlertSubType.CS_LOST_REVENUE]: '/s/article/status-completed-charging-session',
  [AlertSubType.CS_INACTIVITY]: '/s/article/troubleshooting-alerts',
  [AlertSubType.CS_INACTIVITY_PT]: '/s/article/troubleshooting-alerts',
};

const ACTIVATION_OVERVIEW_CHARGING_INFRASTRUCTURE_TAB = 0;
const CHARGE_RECORDS_CP_EXTENDED_VIEW_TAB = 0;

interface IssueDetailLine {
  id: string;
  text?: string;
  translationKey?: string;
  params?: Record<string, string | number>;
}

interface LearnMoreAction {
  type: 'route' | 'external' | 'vayla';
  labelKey: string;
  dataTestId: string;
  leftIcon?: string;
  rightIcon?: string;
  route?: string[];
  queryParams?: Params;
  url?: string;
  promptKey?: string;
}

@Component({
  selector: 'eop-session-alert-details',
  standalone: false,
  templateUrl: './session-alert-details.html',
  styleUrl: './session-alert-details.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionAlertDetails implements OnChanges {
  readonly ChipColor = ChipColor;
  readonly ChipSize = ChipSize;
  readonly ButtonColor = ButtonColor;
  readonly ButtonSize = ButtonSize;

  @Input({ required: true }) alert: Alert;
  @Input() sessionAlertDetails: ChargingSessionAlertDetails | null;
  @Input() description: string | null;
  @Input() partnerId: string;

  issueDetailLines: IssueDetailLine[] = [];
  issueDetailsKey = 'ALERTING.ISSUE_DETAILS_TEXT_UNKNOWN';
  reasonTitleKey: string | null = null;
  learnMoreActions: LearnMoreAction[] = [];
  readonly askVaylaDisabled = signal(false);

  private readonly chatbotService = inject(ChatbotService);
  private readonly translateService = inject(TranslateService);

  ngOnChanges(changes: SimpleChanges): void {
    const alert: Alert = changes['alert'] ? changes['alert'].currentValue : this.alert;
    const sessionAlertDetails: ChargingSessionAlertDetails | null = changes['sessionAlertDetails']
      ? changes['sessionAlertDetails'].currentValue
      : this.sessionAlertDetails;
    const description: string | null = changes['description']
      ? changes['description'].currentValue
      : this.description;
    const partnerId: string = changes['partnerId']
      ? changes['partnerId'].currentValue
      : this.partnerId;

    if (changes['alert']) {
      this.issueDetailsKey = this.computeIssueDetailsKey(alert);
      this.reasonTitleKey = this.computeReasonTitleKey(alert);
    }

    if (changes['alert'] || changes['sessionAlertDetails'] || changes['description']) {
      this.issueDetailLines = this.computeIssueDetailLines(alert, sessionAlertDetails, description);
    }

    if (changes['alert'] || changes['partnerId']) {
      this.learnMoreActions = this.computeLearnMoreActions(alert, partnerId);
    }
  }

  onAskVayla(promptKey: string): void {
    if (!promptKey) {
      return;
    }
    const prompt = this.translateService.instant(promptKey);
    this.askVaylaDisabled.set(true);

    this.chatbotService.openWithPrompt(prompt).finally(() => this.askVaylaDisabled.set(false));
  }

  openKnowledgeBase(url: string): void {
    if (!url) {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private computeIssueDetailLines(
    alert: Alert,
    sessionAlertDetails: ChargingSessionAlertDetails | null,
    description: string | null
  ): IssueDetailLine[] {
    if (this.isRevenueAlert(alert?.alertSubType)) {
      return this.getRevenueIssueDetailLines(alert, sessionAlertDetails, description);
    }

    if (this.isInactivityAlert(alert?.alertSubType)) {
      return this.getInactivityIssueDetailLines(alert, sessionAlertDetails, description);
    }

    return this.getFallbackIssueDetailLines(alert, description);
  }

  private computeIssueDetailsKey(alert: Alert): string {
    const alertSubType = alert?.alertSubType;
    return alertSubType
      ? (ISSUE_DETAILS_KEYS[alertSubType] ?? 'ALERTING.ISSUE_DETAILS_TEXT_UNKNOWN')
      : 'ALERTING.ISSUE_DETAILS_TEXT_UNKNOWN';
  }

  private computeReasonTitleKey(alert: Alert): string | null {
    if (alert?.alertSubType === AlertSubType.CS_MISSED_REVENUE) {
      return 'ALERTING.START_FAILURE_REASONS';
    }
    if (alert?.alertSubType === AlertSubType.CS_LOST_REVENUE) {
      return 'ALERTING.CDR_ERROR_REASONS';
    }
    return null;
  }

  private computeLearnMoreActions(alert: Alert, partnerId: string): LearnMoreAction[] {
    const actions: LearnMoreAction[] = [];

    if (alert?.alertSubType === AlertSubType.CS_MISSED_REVENUE) {
      actions.push({
        type: 'route',
        labelKey: 'ALERTING.SEE_ACTIVATION_OVERVIEW',
        route: ['/monitor/activationOverview'],
        queryParams: this.getActivationOverviewQueryParams(alert),
        rightIcon: 'arrow-right-outlined',
        dataTestId: 'see-activation-overview',
      });
    }

    if (alert?.alertSubType === AlertSubType.CS_LOST_REVENUE) {
      actions.push({
        type: 'route',
        labelKey: 'ALERTING.SEE_CHARGE_RECORDS',
        route: ['/monitor/chargeRecords'],
        queryParams: this.getChargeRecordsQueryParams(alert, partnerId),
        rightIcon: 'arrow-right-outlined',
        dataTestId: 'see-charge-records',
      });
    }

    const knowledgeBaseUrl = this.getKnowledgeBaseUrl(alert?.alertSubType);
    if (knowledgeBaseUrl) {
      actions.push({
        type: 'external',
        labelKey: 'ALERTING.KNOWLEDGE_BASE_ACTION',
        leftIcon: 'help-outlined1',
        url: knowledgeBaseUrl,
        dataTestId: 'knowledge-base',
      });
    }

    const promptKey = VAYLA_PROMPT_KEYS[alert?.alertSubType];
    if (promptKey) {
      actions.push({
        type: 'vayla',
        labelKey: 'ALERTING.ASK_VAYLA_ACTION',
        leftIcon: 'vayla',
        promptKey,
        dataTestId: 'ask-vayla',
      });
    }

    return actions;
  }

  private getActivationOverviewQueryParams(alert: Alert): Params {
    return {
      activeTab: ACTIVATION_OVERVIEW_CHARGING_INFRASTRUCTURE_TAB,
      searchString: alert?.deviceId,
      partnerId: this.partnerId,
    };
  }

  private getChargeRecordsQueryParams(alert: Alert, partnerId: string): Params {
    return {
      activeTab: CHARGE_RECORDS_CP_EXTENDED_VIEW_TAB,
      partnerId,
      evseName: alert?.deviceId,
      processingStatus: ['INVALID', 'ABORTED'],
    };
  }

  private getKnowledgeBaseUrl(alertSubType: AlertSubType): string | null {
    const path = KNOWLEDGE_BASE_PATHS[alertSubType];
    return path ? `${environment.knowledgeBaseUrl}${path}` : null;
  }

  private getRevenueIssueDetailLines(
    alert: Alert,
    details: ChargingSessionAlertDetails | null,
    description: string | null
  ): IssueDetailLine[] {
    const consecutiveCount = details?.revenueDetail?.consecutiveCount;
    if (consecutiveCount !== undefined && consecutiveCount !== null) {
      return [
        {
          id: alert.alertSubType,
          translationKey: this.issueDetailsKey,
          params: {
            count: consecutiveCount,
          },
        },
      ];
    }

    return this.getFallbackIssueDetailLines(alert, description);
  }

  private getInactivityIssueDetailLines(
    alert: Alert,
    details: ChargingSessionAlertDetails | null,
    description: string | null
  ): IssueDetailLine[] {
    const inactivityDetails = details?.inactivityDetail;
    if (!inactivityDetails) {
      return this.getFallbackIssueDetailLines(alert, description);
    }

    return [
      {
        id: inactivityDetails.currentType,
        translationKey: this.issueDetailsKey,
        params: {
          currentType:
            inactivityDetails.currentType === ChargingSessionCurrentType.UNSPECIFIED
              ? ''
              : inactivityDetails.currentType,
          days: inactivityDetails.daysSinceLastSession,
          threshold: inactivityDetails.thresholdDays,
        },
      },
    ];
  }

  private getFallbackIssueDetailLines(alert: Alert, description: string | null): IssueDetailLine[] {
    const text = description || alert?.details;
    return [
      text
        ? {
            id: 'description',
            text,
          }
        : {
            id: 'unknown',
            translationKey: 'ALERTING.ISSUE_DETAILS_TEXT_UNKNOWN',
          },
    ];
  }

  private isRevenueAlert(alertSubType: AlertSubType): boolean {
    return (
      alertSubType === AlertSubType.CS_MISSED_REVENUE ||
      alertSubType === AlertSubType.CS_LOST_REVENUE
    );
  }

  private isInactivityAlert(alertSubType: AlertSubType): boolean {
    return (
      alertSubType === AlertSubType.CS_INACTIVITY || alertSubType === AlertSubType.CS_INACTIVITY_PT
    );
  }
}
